import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  settingsService,
  type EWallet,
  type CreateEWalletInput,
  type UpdateEWalletInput,
} from '@/services/settings.service';
import { settingsKeys } from '@/services/queryKeys';
import { mockEWallets } from '@/mocks/tenantSettings';
import { isArrayResponse, withMockFallback } from '@/utils/queryMock';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useEWallets() {
  return useQuery<EWallet[]>({
    queryKey: settingsKeys.eWallets(),
    queryFn: () =>
      withMockFallback(
        () => settingsService.listEWallets(),
        async () => {
          await sleep(120);
          return mockEWallets;
        },
        { label: 'settings.eWallets', validate: isArrayResponse },
      ),
    retry: 0,
  });
}

export function useCreateEWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateEWalletInput) =>
      withMockFallback(
        () => settingsService.createEWallet(body),
        async () =>
          ({
            _id: `mock-${Date.now()}`,
            tenant_id: 'mock-tenant',
            ...body,
            is_default: !!body.is_default,
          }) as EWallet,
        { label: 'settings.createEWallet' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.eWallets() }),
  });
}

export function useUpdateEWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateEWalletInput }) =>
      withMockFallback(
        () => settingsService.updateEWallet(id, body),
        async () => ({ _id: id, ...body } as unknown as EWallet),
        { label: 'settings.updateEWallet' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.eWallets() }),
  });
}

export function useDeleteEWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      withMockFallback(
        () => settingsService.deleteEWallet(id),
        async () => ({ deleted: true }),
        { label: 'settings.deleteEWallet' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.eWallets() }),
  });
}

export function useSetDefaultEWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      withMockFallback(
        () => settingsService.setDefaultEWallet(id),
        async () => ({ _id: id, is_default: true } as unknown as EWallet),
        { label: 'settings.setDefaultEWallet' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.eWallets() }),
  });
}
